import type { ReactNode } from "react";
import { Box, Button, Typography } from "@mui/material";
import { alpha, darken, type Theme } from "@mui/material/styles";
import AppModal from "@/shared/ui/AppModal";
import { darkDialogContentSx, darkDialogHeaderSx } from "@/shared/ui/dialogSurfaceStyles";

const FOOTER_BUTTON_FONT_SIZE_PX = 9.75;
const FOOTER_BUTTON_HEIGHT_PX = 26;
const FOOTER_BUTTON_RADIUS_PX = 6;

function confirmContentSx(theme: Theme) {
  return {
    paddingTop: "20px",
    paddingRight: "24px",
    paddingBottom: "16px",
    paddingLeft: "24px",
    ...darkDialogContentSx(theme),
  };
}

/** Non-destructive confirm. Dark theme matches Saved Searches "Save Current Search". */
function confirmPrimaryColors(theme: Theme) {
  if (theme.palette.mode !== "dark") {
    return {
      backgroundColor: theme.app.dialog.accentTextColor,
      color: theme.app.dialog.headerTextColor,
      hoverBackgroundColor: theme.app.dialog.accentTextColor,
    };
  }
  const backgroundColor = theme.app.filterBar.filledActionBackgroundColor;
  return {
    backgroundColor,
    color: theme.app.filterBar.advancedButtonTextColor,
    hoverBackgroundColor: darken(backgroundColor, 0.12),
  };
}

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: ReactNode;
  confirmLabel: string;
  cancelLabel: string;
  destructive?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export default function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel,
  cancelLabel,
  destructive = false,
  onConfirm,
  onCancel,
}: Readonly<ConfirmDialogProps>) {
  return (
    <AppModal
      open={open}
      onClose={onCancel}
      title={title}
      size={{ maxWidth: "xs" }}
      headerSx={darkDialogHeaderSx}
      contentSx={confirmContentSx}
      footer={
        <>
          <Box sx={{ flex: 1 }} />
          <Button
            onClick={onCancel}
            variant="outlined"
            size="small"
            sx={(theme) => {
              const outline = theme.app.filter.toggleTab.activeColor;
              return {
                boxSizing: "border-box",
                height: FOOTER_BUTTON_HEIGHT_PX,
                minHeight: FOOTER_BUTTON_HEIGHT_PX,
                maxHeight: FOOTER_BUTTON_HEIGHT_PX,
                py: 0,
                textTransform: "none",
                fontFamily: "var(--font-sans)",
                fontWeight: 500,
                fontSize: FOOTER_BUTTON_FONT_SIZE_PX,
                lineHeight: 1,
                borderRadius: `${FOOTER_BUTTON_RADIUS_PX}px`,
                color: outline,
                borderColor: alpha(outline, 0.5),
                "&:hover": {
                  borderColor: outline,
                  backgroundColor: theme.app.dialog.footerOutlinedHoverBackground,
                  color: outline,
                },
              };
            }}
          >
            {cancelLabel}
          </Button>
          <Button
            onClick={onConfirm}
            variant="contained"
            size="small"
            disableElevation={!destructive}
            // Intentional: confirm button is auto-focused so Enter confirms; keyboard users land on the primary action.
            // oxlint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            sx={(theme) => {
              const deleteShadow = theme.shadows[2];
              const primary = confirmPrimaryColors(theme);
              return {
                boxSizing: "border-box",
                height: destructive ? 24 : FOOTER_BUTTON_HEIGHT_PX,
                minHeight: destructive ? 24 : FOOTER_BUTTON_HEIGHT_PX,
                maxHeight: destructive ? 24 : FOOTER_BUTTON_HEIGHT_PX,
                py: 0,
                textTransform: "none",
                fontFamily: "var(--font-sans)",
                fontStyle: "normal",
                fontWeight: 500,
                fontSize: FOOTER_BUTTON_FONT_SIZE_PX,
                lineHeight: destructive ? "17px" : 1,
                textAlign: "center",
                borderRadius: `${FOOTER_BUTTON_RADIUS_PX}px`,
                ...(destructive
                  ? {
                      width: confirmLabel === "Delete" ? 64 : "auto",
                      minWidth: 64,
                      padding: "3px 10px",
                      backgroundColor: theme.app.dialog.deleteIconColor,
                      color: theme.app.dialog.destructiveButtonTextColor,
                      "&&, &&:hover, &&.Mui-focusVisible, &&:active": {
                        backgroundColor: theme.app.dialog.deleteIconColor,
                        boxShadow: deleteShadow,
                      },
                    }
                  : {
                      backgroundColor: primary.backgroundColor,
                      color: primary.color,
                      boxShadow: theme.app.dialog.footerPrimaryButtonShadow,
                      "&:hover": {
                        backgroundColor: primary.hoverBackgroundColor,
                        boxShadow: theme.app.dialog.footerPrimaryButtonShadow,
                      },
                    }),
              };
            }}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <Typography
        sx={(theme) => ({
          m: 0,
          fontFamily: "var(--font-sans)",
          fontStyle: "normal",
          fontWeight: 400,
          fontSize: "10.5625px",
          lineHeight: "15px",
          color: theme.app.filterBar.barLabelColor,
        })}
      >
        {message}
      </Typography>
    </AppModal>
  );
}
